import {
  Alert,
  AlertColor,
  Snackbar,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Container,
  Content,
  ContentBody,
  ContentBodyLeft,
  ContentBodyRight,
  ContentInput,
  InputLabel,
  ContentInputProfilePhoto,
  ContentInputProfilePhotoIcon,
  ContentTitle,
  Title,
  FieldContainer,
  FieldLabel,
  TagsContainer,
  Tag,
  RemoveButton,
  FolderFieldRowFull,
  InputFieldSimple,
} from "./styles";
import { useNavigate } from "react-router-dom";
import { UserPermissions } from "../../../App";
import CustomDropdown from "../../../Components/CustomDropdown";

interface Props {
  userPermissions: UserPermissions;
}
const CreateExtension: React.FC<Props> = ({ userPermissions }) => {
  const navigate = useNavigate();

  const [username, setUsername] = useState<string>("");
  const [tagInput, setTagInput] = useState<string>("");

  const [extensionData, setExtensionData] = useState<{
    id: string;
    name: string;
    description: string;
    logo?: string;
    tags?: { value: string; color: string }[];
    publisherType?: string;
  }>({
    id: "",
    name: "",
    description: " ",
    logo: undefined,
    tags: [],
    publisherType: "org",
  });

  const [ownerOptions, setOwnerOptions] = useState<any[]>([
    /*{ label: "ResearchX", type: 'user', value: 'research_x', checked: false }*/
  ]);
  const [publisherOrg, setPublisherOrg] = useState<string | undefined>(undefined);
  const publisherOptions = [
    { label: "Organization", value: "org" },
    { label: "User", value: "user" },
  ];

  const onPublisherTypeChange = (values: string[]) => {
    const selected = publisherOptions.find(option => values.includes(option.value));

    setExtensionData(prev => ({
      ...prev,
      publisherType: selected?.value,
    }));
  };

  const getPossibleOwnerList = () => {
    fetch("/api/v1/software/getCreationProperties", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          setOwnerOptions(
            data.possibleOwnerList.map((val: any) => {
              return {
                label: val.name,
                type: val.type,
                value: val.id,
              };
            })
          );
        }
      }).catch((err) => {
        console.log("Cannot get possible owner list:", err);
      })
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSquareClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (png, jpg, etc.)');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const fullBase64String = reader.result as string;

        setExtensionData((prev) => ({
          ...prev,
          logo: fullBase64String,
        }));

        // const rawBase64 = fullBase64String.split(',')[1];
      };

      reader.readAsDataURL(file);
    }
  };

  const [showStatus, setShowStatus] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState("");
  const [statusSeverity, setStatusSeverity] =
    React.useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  const onCreateExtension = async () => {
    if (!username) {
      setStatusMsg(
        `Extension was not created due to an unkown error. Refresh the page and try again.`
      );
      setStatusSeverity("error");
      setShowStatus(true);
      return;
    };
    const toSend = {
      ...extensionData,
      tags: extensionData.tags?.map(tag => tag.value),
      publisherId: extensionData.publisherType === "user" ? username : publisherOrg,
    };
    console.log(toSend);

    fetch("/api/v1/extension/create", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(toSend),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        if (data.success === true) {
          console.log("Created!");
          setStatusMsg("Extension was created! Redirecting in 5 seconds...");
          setStatusSeverity("success");
          setShowStatus(true);
          setTimeout(() => {
            navigate(process.env.PUBLIC_URL + "/Extensions");
          }, 5000);
        } else {
          console.log("Not created: ", data.reason);
          if (data.reason === "Unkown Error")
            setStatusMsg(
              `Extension was not created due to an unkown error. Refresh the page and try again.`
            );
          else setStatusMsg(`Extension was not created: ${data.reason + ""}`);
          setStatusSeverity("error");
          setShowStatus(true);
        }
      }).catch((err) => {
        console.log("Failed to create a extension: ", err);
        setStatusMsg(
          `Extension was not created due to an unkown error. Refresh the page and try again.`
        );
        setStatusSeverity("error");
        setShowStatus(true);
      })
  };

  const isUserPublisherTypeSelected = useMemo(() => {
    return extensionData.publisherType === "user";
  }, [extensionData.publisherType]);

  const removeTag = (index: number) => {
    setExtensionData(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index)
    }));
  };

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedInput = tagInput.trim();

      if (trimmedInput && !extensionData.tags?.some(tag => tag.value === trimmedInput)) {
        setExtensionData(prev => ({
          ...prev,
          tags: [
            ...(prev.tags || []),
            {
              value: trimmedInput,
              color: '#2E933C'
            }
          ]
        }));
        setTagInput("");
      }
    }
  };

  const getUsername = async () => {
    const response = await fetch("/api/v1/account/myAccount", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    if (data.success === true) {
      setUsername(data.username);
    }
  };

  useEffect(() => {
    getPossibleOwnerList();
    getUsername();
  }, [])

  return (
    <Container>
      <Title>
        Create Extension{" "}
        <Button
          onClick={() => {
            onCreateExtension();
          }}
        >
          Create
        </Button>
      </Title>

      <Content>
        <ContentTitle>
          {extensionData.name} {extensionData.name && extensionData.id && `(${extensionData.id})`}
        </ContentTitle>

        <ContentBody style={{ padding: "10px" }}>
          <ContentBodyLeft>
            <ContentInput
              label="Name"
              variant="standard"
              onChange={(ev) =>
                setExtensionData({ ...extensionData, name: ev.target.value })
              }
            />

            <ContentInput
              label="Package ID"
              variant="standard"
              onChange={(ev) => setExtensionData({ ...extensionData, id: ev.target.value })}
            />

            <ContentInput
              id="standard-multiline-static"
              label="Description"
              multiline
              rows={4}
              variant="standard"
              value={extensionData.description}
              onChange={(ev) =>
                setExtensionData({ ...extensionData, description: ev.target.value })
              }
            />

            {/* Tags */}
            <FieldContainer>
              <FieldLabel>Tags</FieldLabel>
              {
                extensionData.tags && extensionData.tags.length > 0 && (
                  <TagsContainer>
                    {extensionData.tags?.map((tag, index) => (
                      <Tag key={index} color={tag.color}>
                        {tag.value}
                        <RemoveButton onClick={() => removeTag(index)}>
                          ×
                        </RemoveButton>
                      </Tag>
                    ))}
                  </TagsContainer>
                )
              }
              <FolderFieldRowFull style={{ flexWrap: "wrap", gap: "8px", alignItems: "flex-start" }}>
                <InputFieldSimple
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInput}
                  placeholder="Type and press Enter to add tag"
                  style={{ flex: "1 1 200px", minWidth: "200px" }}
                />
              </FolderFieldRowFull>
            </FieldContainer>

            <div style={{ display: 'flex', flexDirection: 'column', gap: "1rem", marginTop: "1rem" }}>
              <InputLabel>Publisher</InputLabel>
              <span style={{ display: 'flex', gap: "1rem", width: "100%" }}>
                {/* Publisher Type */}
                <CustomDropdown
                  isMulti={false}
                  options={publisherOptions}
                  selectedValues={extensionData.publisherType ? [extensionData.publisherType] : []}
                  onChange={onPublisherTypeChange}
                  style={{ height: '100%', width: '30%', border: `1px solid #374957` }}
                />

                {/* Publisher ID */}
                <CustomDropdown
                  isMulti={false}
                  disabled={isUserPublisherTypeSelected}
                  options={ownerOptions.map(owner => ({ label: owner.label, value: owner.value }))}
                  selectedValues={isUserPublisherTypeSelected ? [username] : [publisherOrg || ""]}
                  onChange={(values) => setPublisherOrg(values[0])}
                  style={{ height: '100%', width: '70%', border: `1px solid #374957` }}
                />
              </span>
            </div>
          </ContentBodyLeft>

          <ContentBodyRight>
            <ContentInputProfilePhoto onClick={handleSquareClick}>
              <img src={extensionData.logo} alt="Click here to upload the extension logo" />
              <ContentInputProfilePhotoIcon />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </ContentInputProfilePhoto>
          </ContentBodyRight>
        </ContentBody>
      </Content>

      <Snackbar
        open={showStatus}
        autoHideDuration={6000}
        onClose={handleCloseStatus}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateExtension;
